import seedrandom from 'seedrandom';

/**
 * A deterministic random number generator.
 * If no seed is provided, it falls back to Math.random().
 */
export class DeterministicRandom {
    private rng: seedrandom.PRNG;
    private seedValue: string | undefined;

    constructor(seed?: string) {
        this.seedValue = seed;
        this.rng = seedrandom(seed);
    }

    /**
     * Returns a random number between 0 and 1.
     */
    next(): number {
        return this.rng();
    }

    /**
     * Returns a random number between min and max.
     */
    range(min: number, max: number): number {
        return min + this.next() * (max - min);
    }

    /**
     * Returns a random integer between min and max (inclusive).
     */
    int(min: number, max: number): number {
        return Math.floor(this.range(min, max + 1));
    }

    /**
     * Picks a random element from an array.
     */
    pick<T>(array: T[]): T {
        return array[this.int(0, array.length - 1)];
    }

    /**
     * Returns true with a given probability (0 to 1).
     */
    chance(probability: number): boolean {
        return this.next() < probability;
    }

    /**
     * Returns the current seed.
     */
    getSeed(): string | undefined {
        return this.seedValue;
    }
}

// Global utility for quick access
let activeRNG = new DeterministicRandom();

export const setGlobalSeed = (seed?: string) => {
    activeRNG = new DeterministicRandom(seed);
};

/**
 * Returns a deterministic random number generator instance.
 */
export const getRNG = () => activeRNG;

export const random = {
    next: () => activeRNG.next(),
    range: (min: number, max: number) => activeRNG.range(min, max),
    int: (min: number, max: number) => activeRNG.int(min, max),
    pick: <T>(array: T[]) => activeRNG.pick(array),
    chance: (p: number) => activeRNG.chance(p)
};
